/**
 * Plan price display: Apple priceString on iOS IAP, INR on Android/web.
 */

import React from 'react';
import { cn } from '@/utils/cn';
import type { Plan, PlanBillingPeriod } from '@/data/membershipPlansData';
import { getPlanDisplayPrice } from '@/data/membershipPlansData';
import { getRcPackageIdForBackendPlan } from '@/data/iapProductMapping';
import type { IosPackagePriceMap } from '@/services/revenueCatService';

export interface MembershipPlanPriceProps {
  plan: Plan;
  billingPeriod: PlanBillingPeriod;
  categoryName?: string | null;
  usesAppleIap: boolean;
  iosPrices?: IosPackagePriceMap;
  iosPricesLoading?: boolean;
  className?: string;
}

function PriceShimmer({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block h-12 w-36 lg:h-14 lg:w-44 rounded-xl bg-[#2C2C2E] animate-pulse',
        className,
      )}
      aria-hidden
    />
  );
}

export const MembershipPlanPrice: React.FC<MembershipPlanPriceProps> = ({
  plan,
  billingPeriod,
  categoryName,
  usesAppleIap,
  iosPrices,
  iosPricesLoading = false,
  className,
}) => {
  const backendPlanId =
    billingPeriod === 'annually' ? plan.annualPlanId : plan.quarterlyPlanId;
  const rcPackageId = backendPlanId
    ? getRcPackageIdForBackendPlan(backendPlanId, categoryName)
    : null;
  const applePrice = rcPackageId && iosPrices ? iosPrices[rcPackageId] : undefined;

  if (usesAppleIap) {
    if (iosPricesLoading && !applePrice) {
      return <PriceShimmer className={className} />;
    }
    if (applePrice) {
      return (
        <span
          className={cn(
            'text-5xl lg:text-6xl font-black text-white tracking-tighter',
            className,
          )}
        >
          {applePrice}
        </span>
      );
    }
    if (!iosPricesLoading) {
      const fallbackInr = getPlanDisplayPrice(plan, billingPeriod, categoryName);
      return (
        <div className={cn('flex flex-col gap-1', className)}>
          <span className="text-5xl lg:text-6xl font-black text-white tracking-tighter">
            ₹{fallbackInr.toLocaleString()}
          </span>
          <span className="text-[10px] font-medium text-[#8E8E93]">
            Final price confirmed at checkout
          </span>
        </div>
      );
    }
    return <PriceShimmer className={className} />;
  }

  const price = getPlanDisplayPrice(plan, billingPeriod, categoryName);
  return (
    <span
      className={cn(
        'text-5xl lg:text-6xl font-black text-white tracking-tighter',
        className,
      )}
    >
      ₹{price.toLocaleString()}
    </span>
  );
};
